import { Typography } from "@mui/material";
import { CoreTextField } from "@verity/components/core";
import { ChangeEventHandler, Dispatch, ReactNode, SetStateAction } from "react";
import i18n from "../../../../../i18n";
import { DEFAULT_PLATFORM, PLATFORM_OPTIONS } from "../../constants";
import { VideoIcon } from "../assets";
import ContentWrapper from "../components/ContentWrapper";
import { FormAutoComplete } from "../components/FormAutoComplete";
import FormTextField from "../components/FormTextField";
import { ConferencePlatformErrorsType, ConferencePlatformType } from "../types";
import styles from "./Form.module.css";

const PLATFORM_ICON_RECORD = PLATFORM_OPTIONS.reduce(
	(acc, { label, icon }) => {
		acc[label] = icon;
		return acc;
	},
	{} as Record<(typeof PLATFORM_OPTIONS)[number]["label"], (typeof PLATFORM_OPTIONS)[number]["icon"]>,
);

type ConferencePlatformFormProps = {
	formValues: ConferencePlatformType;
	setFormValues: Dispatch<SetStateAction<ConferencePlatformType>>;
	errors: ConferencePlatformErrorsType;
	setErrors: Dispatch<SetStateAction<ConferencePlatformErrorsType>>;
};

export default function ConferencePlatformForm({
	formValues,
	setFormValues,
	errors,
	setErrors,
}: ConferencePlatformFormProps) {
	function clearError<K extends keyof ConferencePlatformType>(key: K) {
		setErrors((prev) => ({ ...prev, [key]: "" }));
	}

	function changeFactory<K extends keyof ConferencePlatformType>(
		key: K,
	): ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> {
		return (e) => {
			setFormValues((prev) => ({
				...prev,
				[key]: e.target.value,
			}));
			clearError(key);
		};
	}

	function getSelectedPlatformIcon(): ReactNode | undefined {
		const platform = PLATFORM_OPTIONS.find((p) => p.value === formValues.videoPlatform);
		if (!platform) return undefined;
		return platform.icon;
	}

	return (
		<ContentWrapper
			title={i18n.t(`candidate.scheduleInterview.platformStep.title`)}
			description={i18n.t(`candidate.scheduleInterview.platformStep.description`)}
		>
			<div className={styles.form}>
				<FormAutoComplete
					title={i18n.t(`candidate.scheduleInterview.platformStep.fields.videoPlatform.label`)}
					placeholder={i18n.t(`candidate.scheduleInterview.platformStep.fields.videoPlatform.placeholder`)}
					value={PLATFORM_OPTIONS.find(({ value }) => value === formValues.videoPlatform)?.label ?? ""}
					onChange={(_, videoPlatform) => {
						setFormValues((prev) => ({
							...prev,
							videoPlatform:
								PLATFORM_OPTIONS.find(({ label }) => label === videoPlatform)?.value ?? DEFAULT_PLATFORM.value,
						}));
						clearError("videoPlatform");
					}}
					options={PLATFORM_OPTIONS.map(({ label }) => label)}
					startIcon={getSelectedPlatformIcon() || <VideoIcon />}
					optionIcons={PLATFORM_ICON_RECORD}
					error={errors.videoPlatform}
				/>
				<FormTextField
					title={i18n.t(`candidate.scheduleInterview.platformStep.fields.meetingLink.label`)}
					placeholder={i18n.t(`candidate.scheduleInterview.platformStep.fields.meetingLink.placeholder`)}
					value={formValues.meetingLink}
					handleChange={changeFactory("meetingLink")}
					error={errors.meetingLink}
				/>
				<>
					<div className={styles.largeValueText}>
						<Typography>{i18n.t(`candidate.scheduleInterview.platformStep.fields.additionalNotes.label`)}</Typography>
					</div>
					<CoreTextField
						className={styles.notes + " " + styles.span_2}
						multiline
						minRows={4}
						maxRows={4}
						fullWidth
						placeholder={i18n.t(`candidate.scheduleInterview.platformStep.fields.additionalNotes.placeholder`)}
						value={formValues.notes}
						onChange={changeFactory("notes")}
						error={errors.notes}
					/>
				</>
			</div>
		</ContentWrapper>
	);
}
